import BaseStore from "../base";
import { action, computed } from "mobx";
import { v4 as uuid } from "uuid";
import http from "@app/lib/http";
import ReportDocument from "@app/state/model/report-document/report-document";
import ReportDocumentSection from "@app/state/model/report-document/report-document-section";
import notify from "@app/components/notify";
import events from "@app/lib/store/events";
import { Role, CerGenerationStatus } from "@app/constants";

import sessionStore from "@app/state/store/session";
import report from "@app/state/store/report";

const STAGGER_DOCS = uuid();
const STAGGER_DOCUMENT = uuid();
const STAGGER_SECTIONS = uuid();

export class ReportDocuments extends BaseStore {
    observable() {
        return {
            projectId: null,
            clientId: null,
            reportDocument: null,
            reportDocuments: [],
            reportDocumentSections: [],
            savingSections: false,
            cerPolling: false,
        };
    }

    // Not observable — cleared by stopCerPolling(), which reset() cannot reach.
    _cerPollTimer = null;

    getAPIBasePath() {
        if (this.projectId) {
            return `/project/${this.projectId}`;
        }

        if (this.clientId) {
            return `/client/${this.clientId}`;
        }

        return null;
    }

    @action
    async setProjectId(projectId) {
        if (this.projectId !== projectId) {
            this.reset();
            this.projectId = projectId;
        }
    }

    @action
    async setClientId(clientId) {
        if (this.clientId !== clientId) {
            this.reset();
            this.clientId = clientId;
        }
    }

    @action
    async loadReportDocuments(params) {
        if (!this.projectId && !this.clientId) {
            return;
        }

        try {
            const basePath = this.getAPIBasePath();
            if (basePath) {
                const { data } = await http
                    .get(`${basePath}/report-documents/list?metaData=${params?.metaData}`)
                    .stagger(STAGGER_DOCS);

                this.reportDocuments = data.map((doc) => new ReportDocument(doc));
            }
        } catch (ex) {
            notify.error(ex.response?.data?.error || "Cannot get report documents");
        }
    }

    @action
    async getReportDocuments({ projectId, clientId, metaData }) {
        if (!projectId && !clientId) {
            return;
        }

        try {
            const basePath = projectId ? `/project/${projectId}` : `/client/${clientId}`;
            const { data } = await http
                .get(`${basePath}/report-documents/list?metaData=${metaData}`)
                .stagger(STAGGER_DOCS);

            return data.map((doc) => new ReportDocument(doc));
        } catch (ex) {
            notify.error(ex.response?.data?.error || "Cannot get report documents");
        }
    }

    @action
    async getClientReportDocuments(clientId) {
        if (!clientId) {
            return [];
        }

        try {
            const { data } = await http.get(`/client/${clientId}/report-documents/list`);
            return data.map((doc) => new ReportDocument(doc));
        } catch (ex) {
            notify.error(ex.response?.data?.error || "Cannot get report documents");
        }
    }

    @action
    async getReportDocument(id) {
        if (!this.projectId && !this.clientId) {
            return;
        }

        if (!id) {
            return;
        }

        // reset the current document when loading a new one
        if (this.reportDocument && this.reportDocument._id !== id) {
            this.unload();
        }

        try {
            const basePath = this.getAPIBasePath();
            if (basePath) {
                const { data } = await http
                    .get(`${basePath}/report-documents/${id}`)
                    .stagger(STAGGER_DOCUMENT);

                if (data?._id) {
                    return new ReportDocument(data);
                }
            }
        } catch (ex) {
            notify.error(ex.response?.data?.error || "Cannot get report document");
        }
    }

    @action
    async updateCurrentReportDocumentMetaData(id) {
        const reportDocument = await this.getReportDocument(id);

        // the current document can be unloaded while the request is in flight
        if (reportDocument?.metaData && this.reportDocument?._id === id) {
            this.reportDocument.metaData = reportDocument.metaData;
        }
    }

    @action
    async load(id) {
        try {
            this.reportDocument = await this.getReportDocument(id);

            if (this.reportDocument) {
                // load the sections
                await this.getReportDocumentSections(id);
            }
        } catch (ex) {
            notify.error(ex.response?.data?.error || "Cannot load report document");
        }
    }

    @action
    async unload() {
        this.reportDocument = null;
        this.reportDocumentSections = [];
    }

    @action
    async refreshCerGeneration(id) {
        if (!id || this.reportDocument?._id !== id) {
            return;
        }

        const fresh = await this.getReportDocument(id);

        if (fresh && this.reportDocument?._id === id) {
            this.stopCerPolling();
            this.reportDocument.cerRunId = fresh.cerRunId;
            this.reportDocument.cerGeneration = fresh.cerGeneration;
        }
    }

    /**
     * Poll the report document while its CER generation run is in progress, so the
     * UI can render a live progress banner. Patches only `cerGeneration`, leaving
     * sections and editing state untouched, until the run reaches a terminal state;
     * on COMPLETED it emits the existing viewer-reload event to pull the freshly
     * generated sections in.
     *
     * Separate from the wizard's `pollRun` (state/store/cer-config.js), which only
     * notifies — this is what drives the banner on the document itself.
     */
    @action
    pollCerGeneration(id, intervalMs = 5000) {
        if (!id || this.cerPolling) {
            return;
        }

        const status = this.reportDocument?.cerGeneration?.status;
        if (
            !this.reportDocument?.cerRunId ||
            status === CerGenerationStatus.COMPLETED ||
            status === CerGenerationStatus.FAILED
        ) {
            return;
        }

        this.cerPolling = true;

        const tick = async () => {
            const fresh = await this.getReportDocument(id);

            // A different document was opened while we were in-flight — stop.
            if (!this.reportDocument || this.reportDocument._id !== id) {
                this.stopCerPolling();
                return;
            }

            if (fresh?.cerGeneration) {
                this.reportDocument.cerGeneration = fresh.cerGeneration;

                const freshStatus = fresh.cerGeneration.status;
                if (freshStatus === CerGenerationStatus.COMPLETED) {
                    this.stopCerPolling();
                    // Sections now exist upstream — reload the document + sections.
                    events.emit("reportDocument.viewer.reload");
                    return;
                }
                if (freshStatus === CerGenerationStatus.FAILED) {
                    // Keep the banner (it renders the error) and stop polling.
                    this.stopCerPolling();
                    return;
                }
            }

            // Still generating (or a transient fetch miss) — keep polling.
            this._cerPollTimer = setTimeout(tick, intervalMs);
        };

        tick();
    }

    @action
    stopCerPolling() {
        if (this._cerPollTimer) {
            clearTimeout(this._cerPollTimer);
            this._cerPollTimer = null;
        }
        this.cerPolling = false;
    }

    @action
    async delete(id) {
        const basePath = this.getAPIBasePath();

        try {
            await http.delete(`${basePath}/report-documents/${id}`);
            events.emit("report-document.delete", id);
        } catch (ex) {
            notify.error(ex.response?.data?.error || "Cannot delete report document");
        }
    }

    @action
    async create(reportDocument) {
        if (!reportDocument) {
            return;
        }

        const basePath = this.getAPIBasePath();

        this.unload();

        try {
            let { data } = await http.put(`${basePath}/report-documents`, {
                reportDocument,
            });

            this.reportDocument = new ReportDocument(data);
            return this.reportDocument;
        } catch (ex) {
            notify.error(ex.response?.data?.error || "Cannot create report document");
            return null;
        }
    }

    @action
    async update(id, reportDocument) {
        if (!reportDocument) {
            return;
        }

        const basePath = this.getAPIBasePath();

        try {
            let { data } = await http.post(`${basePath}/report-documents/${id}`, {
                reportDocument,
            });

            this.reportDocument = new ReportDocument(data);
            events.emit("reportDocument.viewer.reload");
            events.emit("reportDocumentList.reload");
        } catch (ex) {
            notify.error(ex.response?.data?.error || "Cannot update report document");
        }
    }

    @action
    async updateReportDocument(type, entry) {
        switch (type) {
            case "create":
                return await this.create(entry);
            case "update":
                return await this.update(entry._id, entry);
            case "delete":
                return await this.delete(entry._id);
            default:
                return;
        }
    }

    @action
    async getReportDocumentSections(reportDocumentId) {
        try {
            const basePath = this.getAPIBasePath();
            if (basePath) {
                let { data } = await http
                    .get(`${basePath}/report-documents/${reportDocumentId}/sections`)
                    .stagger(STAGGER_SECTIONS);

                this.reportDocumentSections = data.map(
                    (section) => new ReportDocumentSection(section),
                );
                return this.reportDocumentSections;
            }
        } catch (ex) {
            notify.error(ex.response?.data?.error || "Cannot get report document sections");
        }
    }

    @action
    async saveReportDocumentSections(reportDocumentId, sections) {
        const basePath = this.getAPIBasePath();

        this.savingSections = true;

        try {
            let { data } = await http.post(
                `${basePath}/report-documents/${reportDocumentId}/sections`,
                {
                    sections,
                },
            );

            if (this.reportDocument?._id === reportDocumentId) {
                this.reportDocumentSections = data.map(
                    (section) => new ReportDocumentSection(section),
                );
            }

            return this.reportDocumentSections;
        } catch (ex) {
            notify.error(ex.response?.data?.error || "Cannot save report document sections");
        } finally {
            this.savingSections = false;
        }
    }

    @computed
    get canUserStartReview() {
        const isPrivileged = sessionStore.user.roles.find((role) =>
            [Role.ACCOUNT_ADMIN, Role.ADMIN, Role.PROJECT_MANAGER].includes(role),
        );
        if (isPrivileged) {
            return true;
        } else {
            // check the project roles
            const projectRole = report.project.userRoles.find((role) =>
                [Role.PROJECT_MANAGER, Role.LEAD_AUTHOR].includes(role),
            );

            return !!projectRole;
        }
    }

    @action
    async review(id, options = {}) {
        const basePath = this.getAPIBasePath();

        try {
            const { data } = await http.post(`${basePath}/report-documents/${id}/review`, {
                includeOnlyReviewed: options.includeOnlyReviewed,
            });
            this.reportDocument = new ReportDocument(data);

            // reload the report document view
            events.emit("reportDocument.viewer.reload");
            events.emit("reportDocumentList.reload");
        } catch (ex) {
            notify.error(ex.response?.data?.error || "Report document review error!");
        }
    }

    /**
     * Check if the current user has reviewed all sections of the document
     */
    hasSectionsPendingReview() {
        const userId = sessionStore.user._id;
        const hasPendingSections = this.reportDocumentSections.some((section) => {
            for (const reviewer of section.reviewers ?? []) {
                if (reviewer._id === userId && !reviewer.reviewed) {
                    return true;
                }
            }

            return false;
        });

        return hasPendingSections;
    }

    @computed
    get citationsSet() {
        const citations = new Set();

        this.reportDocumentSections.forEach((section) =>
            section.citations?.forEach((citation) => citations.add(citation)),
        );

        return Array.from(citations);
    }

    @computed
    get tableSet() {
        const tables = new Set();

        this.reportDocumentSections.forEach((section) =>
            section.tables?.forEach((table) => tables.add(table)),
        );

        return Array.from(tables);
    }

    @computed
    get figureSet() {
        const figures = new Set();

        this.reportDocumentSections.forEach((section) =>
            section.figures?.forEach((figure) => figures.add(figure)),
        );

        return Array.from(figures);
    }

    @action getUserReviewer(reviewers) {
        const reviewer = reviewers?.find((reviewer) => reviewer._id === sessionStore.user._id);
        return reviewer;
    }

    @action canAccess(reportDocument) {
        let access = true;

        if (!reportDocument) {
            access = false;
        } else {
            const isAdminRole = sessionStore.user.roles.find((role) =>
                [Role.ACCOUNT_ADMIN, Role.ADMIN].includes(role),
            );

            if (!isAdminRole) {
                const isOnlyReviewerProjectRole =
                    report.project.userRoles.length === 1 &&
                    report.project.userRoles[0] === Role.REVIEWER;

                const isOnlyClientProjectRole =
                    report.project.userRoles.length === 1 &&
                    report.project.userRoles[0] === Role.CLIENT;

                const isDocumentReviewer = reportDocument.reviewConfig?.stages?.find((stage) =>
                    this.getUserReviewer(stage.reviewers),
                );

                const isCurrentStageReviewer = this.getUserReviewer(
                    reportDocument.review?.stage?.reviewers,
                );

                const hadCompletedReview = reportDocument.review?.history?.find((history) =>
                    this.getUserReviewer(history.reviewers),
                );

                if (isOnlyReviewerProjectRole) {
                    if (isDocumentReviewer) {
                        if (!hadCompletedReview && !isCurrentStageReviewer) {
                            access = false;
                        }
                    } else {
                        access = false;
                    }
                } else if (isOnlyClientProjectRole) {
                    if (isDocumentReviewer) {
                        if (!hadCompletedReview && !isCurrentStageReviewer) {
                            access = false;
                        }
                    } else {
                        access = false;
                    }
                }
            }
        }

        return access;
    }
}

export default new ReportDocuments();
